import React from 'react';

const Services = () => {
  const services = [
    {
      title: "Data & Automation Audit",
      price: "Starting at $1,500",
      summary: "A short, focused engagement to identify your highest-ROI automation and reporting opportunities.",
      icon: (
        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
      items: [
        "Workflow + systems review (1-2 hours)",
        "Written opportunity report with priorities",
        "Realistic effort and cost estimates",
        "Credited toward a future project"
      ]
    },
    {
      title: "Custom Dashboards & Reporting",
      price: "Starting at $8,000",
      summary: "Replace manual spreadsheet reports with dashboards your team actually uses.",
      icon: (
        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      items: [
        "Power BI, Tableau, or custom web dashboards",
        "Automated refresh from your source systems",
        "Role-based access and secure sharing",
        "Training so your team can self-serve"
      ]
    },
    {
      title: "AI Automation & Workflow",
      price: "Starting at $10,000",
      summary: "Custom AI tools that automate your repetitive, high-friction work.",
      icon: (
        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      items: [
        "Document summarization and extraction",
        "Internal chat assistants on your own data",
        "Email, ticket, and intake triage automation",
        "Integrations with CRMs, ERPs, and email"
      ]
    },
    {
      title: "Data Platforms & Pipelines",
      price: "Starting at $12,000",
      summary: "The plumbing that connects your tools and powers everything above.",
      icon: (
        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
        </svg>
      ),
      items: [
        "API integrations and scheduled data pulls",
        "Cloud data warehouses (Snowflake, BigQuery, Postgres)",
        "ETL pipelines with monitoring and alerts",
        "Data cleanup, validation, and transformation"
      ]
    }
  ];

  return (
    <section id="services" className="bg-gradient-to-b from-gray-50 to-white py-20 lg:py-28">
      {/* Section Header */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white py-16 mb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">Our Services</h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-cyan-400 mx-auto mb-6"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Fixed-price engagements. Most projects delivered in 4-8 weeks.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid gap-8 md:grid-cols-2">
          {services.map((service) => (
            <div
              key={service.title}
              className="bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 flex flex-col"
            >
              {/* Icon */}
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-xl mb-6">
                {service.icon}
              </div>

              {/* Title + Price */}
              <h3 className="text-2xl font-bold text-gray-900 mb-2 leading-relaxed">
                {service.title}
              </h3>
              <p className="text-blue-600 font-semibold mb-4">{service.price}</p>

              {/* Divider */}
              <div className="w-12 h-1 bg-gradient-to-r from-blue-600 to-cyan-600 mb-6"></div>

              {/* Summary */}
              <p className="text-gray-600 mb-6 leading-relaxed">{service.summary}</p>

              {/* Services List */}
              <ul className="space-y-3 flex-grow">
                {service.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start text-gray-600 leading-relaxed">
                    <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3"></div>
                    <span className="text-base">{item}</span>
                  </li>
                ))}
              </ul>

              {/* Call to Action */}
              <div className="mt-8 pt-6 border-t border-gray-100">
                <a
                  href="#contact"
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold transition-colors duration-200 group"
                >
                  Start a Conversation
                  <svg className="w-4 h-4 ml-2 transition-transform duration-200 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-xl text-gray-600 mb-8">
            Not sure which fits? Start with a free 30-minute call.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="#contact"
              className="inline-flex items-center bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-4 px-8 rounded-full text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-blue-500 focus:ring-offset-2"
            >
              Send Us a Message
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
            <a
              href="tel:+19286070710"
              className="inline-flex items-center border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white font-semibold py-4 px-8 rounded-full text-lg transition-all duration-300"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              Call (928) 607-0710
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
